import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { GridCallbackDetails, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { IMenu } from 'src/@types/apiResponseTypes';
import { CheckboxTree } from 'src/components/custom-tree-view';
import { Page } from 'src/components/Page';
import Scrollbar from 'src/components/scrollbar/Scrollbar';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import { IGroupGridRow } from 'src/hooks/useGetGridCol';
import { ContentButtonCreate } from 'src/layouts/form/FormButton';
import { IGroup, useStores } from 'src/models';
import { menuApi } from 'src/service';

import MenuGroupGrid from '../group/MenuGroupGrid';

/**
 * ## Menu 설명
 * - 메뉴 관리 페이지
 */
export const Menu = observer(() => {
  const rootStore = useStores();
  const { commonCodeStore } = rootStore;
  const { callApiWithLoading } = useCallApiWithLoading();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const MENU_TYPES = commonCodeStore.getCCodeByName('MENU_TYPE_CD');

  const [selectedUserGroup, setSelectedUserGroup] = useState<IGroup | null>(null);
  const [menus, setMenus] = useState<IMenu[]>([]);
  const [menuTreeItems, setMenuTreeItems] = useState<TreeViewBaseItem[]>([]);
  const [selectedMenuSids, setSelectedMenuSids] = useState<string[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  useEffect(() => {
    getMenus();
  }, []);

  useEffect(() => {
    // 메뉴 목록이 변경되면 트리 아이템으로 변환
    const treeItems = convertMenusToTreeItems(menus);
    setMenuTreeItems(treeItems);
  }, [menus]);

  // 메뉴 목록 조회
  const getMenus = async () => {
    try {
      await callApiWithLoading(async () => {
        const result = await menuApi.getMenus();
        if (result) {
          setMenus(result.data);
          return;
        }

        // 메뉴 목록 조회 실패 시
        enqueueSnackbar('메뉴 목록을 조회할 수 없습니다.', { variant: 'error' });
      });
    } catch (error) {
      console.error(error);
    }
  };

  // 선택된 권한 그룹에 대한 메뉴 목록 조회
  const getMenusByUserGroupSid = async (userGroupSid: number) => {
    try {
      await callApiWithLoading(async () => {
        const result = await menuApi.getMenusByGroupSid(userGroupSid);

        if (result) {
          setSelectedMenuSids(result.data.map((menu) => menu.menuSid.toString()));
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  // group 테이블에서 그룹 선택 시
  const handleRowSelectionModelChange = (
    newSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails<'filter'>,
  ) => {
    setRowSelectionModel(newSelectionModel);

    // 선택한 그룹의 row 데이터를 저장
    const selectedUserGroupSid = newSelectionModel[0];
    if (selectedUserGroupSid) {
      const selectedUserGroupRow = details.api.getRow(selectedUserGroupSid) as IGroupGridRow;
      setSelectedUserGroup(selectedUserGroupRow);
      getMenusByUserGroupSid(selectedUserGroupRow.oprtrGroupSid);
    } else {
      setSelectedUserGroup(null);
    }
  };

  // 그룹별 메뉴 목록 업데이트
  const updateMenusByUserGroupSid = async () => {
    try {
      await callApiWithLoading(async () => {
        if (selectedUserGroup === null) {
          enqueueSnackbar(t('group.message.selectGroup'), { variant: 'error' });
          return;
        }

        const result = await menuApi.updateMenusByUserGroupSid(
          selectedUserGroup.oprtrGroupSid,
          selectedMenuSids.map((sid) => parseInt(sid, 10)),
        );
        if (result) {
          enqueueSnackbar('메뉴 목록이 수정되었습니다.', { variant: 'success' });
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  // 메뉴 데이터를 트리 아이템으로 변환하는 함수
  const convertMenusToTreeItems = (menus: IMenu[]): TreeViewBaseItem[] => {
    // 하위 메뉴를 재귀적으로 추가하는 함수
    const appendChildrenMenu = (parentSid: number | null): TreeViewBaseItem[] => {
      const children = menus.filter((menu) => menu.menuPsid === parentSid);

      return children
        .map((menu) => ({
          ...menu,
          id: menu.menuSid.toString(),
          label: menu.menuNm,
          children: appendChildrenMenu(menu.menuSid),
        }))
        .sort((a, b) => a.ordr - b.ordr);
    };

    // 루트 메뉴 (menuPsid가 null인 메뉴)부터 시작
    const rootMenus = appendChildrenMenu(null);

    return rootMenus;
  };

  return (
    <Page>
      <ContentsContainer>
        {/* 권한 그룹 선택 테이블 */}
        <Content>
          <Scrollbar>
            <StyledGroupTable
              checkboxSelection
              disableMultipleRowSelection
              disableRowSelectionOnClick={false}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              rowSelectionModel={rowSelectionModel}
              slots={{
                toolbar: () => null,
                footer: () => null,
              }}
            />
          </Scrollbar>
        </Content>
        {/* 선택한 권한 그룹에 대한 메뉴 체크리스트 */}
        <Content>
          <Scrollbar>
            <ContentTop>
              <Typography variant="h4">
                {selectedUserGroup?.oprtrGroupSid
                  ? `${t('menu.message.selectedGroup')}: ${selectedUserGroup.oprtrGroupSid} - ${
                      selectedUserGroup.oprtrGroupNm
                    }`
                  : ''}
              </Typography>
              <ContentButtonCreate onClick={updateMenusByUserGroupSid}>
                {t('common.button.save')}
              </ContentButtonCreate>
            </ContentTop>
            {selectedUserGroup !== null ? (
              <CheckboxTree
                items={menuTreeItems}
                selectedItemIds={selectedMenuSids}
                setSelectedItemIds={setSelectedMenuSids}
                defaultExpandedItems={menuTreeItems.map((item) => item.id)}
              />
            ) : (
              <EmptyUserGroupBox>
                <Typography variant="h3">{t('menu.message.selectGroup')}</Typography>
              </EmptyUserGroupBox>
            )}
          </Scrollbar>
        </Content>
      </ContentsContainer>
    </Page>
  );
});

export default Menu;

const ContentsContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'grid',
  gap: theme.spacing(2),
  overflow: 'auto',

  // desktop styling
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '3fr 7fr',
    height: '100%',
  },
}));

const Content = styled('div')(({ theme }) => ({
  width: '100%',
  minHeight: '300px',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.background.paper,
}));

const ContentTop = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  position: 'sticky',
  top: 0,
  background: theme.palette.background.paper,
  zIndex: 1,
  boxShadow: theme.customShadows.z1,
}));

const StyledGroupTable = styled(MenuGroupGrid)(({ theme }) => ({
  padding: `${theme.spacing(2)}, ${theme.spacing(4)}`,
}));

const EmptyUserGroupBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexGrow: 1,
}));
